import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './FileActionsMenu.module.scss';

export interface FileActionsMenuItem {
    name: string;
    onClick: () => void;
}

interface FileActionsMenuProps {
    items: FileActionsMenuItem[];
}

/**
 * File-card kebab menu. Dropdown chrome, right-anchored positioning, and
 * close-on-outside-click/scroll mirror Data Room's file three-dots menu
 * (modules/DataRoom/Components/FileExplorer/Components/File/File.tsx +
 * File.module.scss's `.OtherOptions`) — portaled to document.body since this
 * tree's scroll containers (Library's file list, the chat scroll area) clip
 * an in-flow absolute dropdown. Trigger button is unchanged from before.
 */
const FileActionsMenu: React.FC<FileActionsMenuProps> = ({ items }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, right: 0 });
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const calculatePosition = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        // Right-anchored so the (wider) "Add to Reports" label never clips off the
        // right viewport edge. Unlike File.tsx's formula, no window.scrollY here:
        // the dropdown is position:fixed (viewport-relative), so adding scrollY
        // double-counts on a scrolled page; any scroll closes the menu anyway.
        setPosition({
            top: rect.bottom,
            right: Math.max(window.innerWidth - rect.right, 8),
        });
    };

    useEffect(() => {
        if (isOpen) calculatePosition();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleScroll = () => setIsOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [isOpen]);

    // Closes on a click outside BOTH the trigger and the portaled dropdown — the
    // shared useOutsideClick hook only checks a single ref, which can't express
    // "outside this OR that", so this stays a local listener (same shape as
    // File.tsx's own handleClickOutside).
    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    if (items.length === 0) return null;

    const handleItemClick = (item: FileActionsMenuItem) => {
        item.onClick();
        setIsOpen(false);
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={styles.Trigger}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label="Open actions menu"
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                &#x22EE;
            </button>
            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={styles.Dropdown}
                        style={{ top: position.top, right: position.right }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {items.map((item) => (
                            <div key={item.name} className={styles.Item} onClick={() => handleItemClick(item)}>
                                {item.name}
                            </div>
                        ))}
                    </div>,
                    document.body,
                )}
        </>
    );
};

export default FileActionsMenu;
